/**
 * OAuth popup orchestration on the SPA side.
 *
 * Opens Google's auth URL in a popup, listens for postMessage from the
 * callback page running on the Worker host, returns the resulting token
 * payload to the caller. The caller is responsible for encrypting + persisting.
 */

import { config } from "./config.js";

export interface GoogleOAuthRequest {
  scope: string;
  /** Additional URL params; e.g. login_hint, hd. */
  extraParams?: Record<string, string>;
}

export interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token?: string;
  scope: string;
  token_type: string;
}

function randomState(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

function buildGoogleAuthUrl(req: GoogleOAuthRequest, state: string): string {
  const u = new URL("https://accounts.google.com/o/oauth2/v2/auth");
  u.searchParams.set("client_id", config.googleOauthClientId);
  u.searchParams.set("redirect_uri", config.googleOauthRedirectUri);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("scope", req.scope);
  u.searchParams.set("access_type", "offline");
  u.searchParams.set("prompt", "consent");
  u.searchParams.set("include_granted_scopes", "true");
  u.searchParams.set("state", state);
  for (const [k, v] of Object.entries(req.extraParams ?? {})) {
    u.searchParams.set(k, v);
  }
  return u.toString();
}

export function connectGoogle(
  req: GoogleOAuthRequest,
): Promise<GoogleTokenResponse> {
  return new Promise((resolve, reject) => {
    const state = randomState();
    const url = buildGoogleAuthUrl(req, state);
    const popup = window.open(
      url,
      "agencylens-oauth-google",
      "width=520,height=640",
    );
    if (!popup) {
      reject(new Error("popup_blocked"));
      return;
    }

    const allowedOrigins = new Set<string>([
      new URL(config.googleOauthRedirectUri).origin,
    ]);

    let resolved = false;
    const onMessage = (event: MessageEvent) => {
      if (!allowedOrigins.has(event.origin)) return;
      const data = event.data as
        | { type?: string; state?: string; tokens?: GoogleTokenResponse; error?: string; provider?: string }
        | null;
      if (!data || data.provider !== "google") return;
      if (data.state !== state) return;
      if (data.type === "agencylens:oauth-success" && data.tokens) {
        cleanup();
        resolve(data.tokens);
      } else if (data.type === "agencylens:oauth-error") {
        cleanup();
        reject(new Error(data.error || "oauth_failed"));
      }
    };

    const pollClosed = window.setInterval(() => {
      if (popup && popup.closed && !resolved) {
        cleanup();
        reject(new Error("popup_closed"));
      }
    }, 500);

    function cleanup() {
      resolved = true;
      window.removeEventListener("message", onMessage);
      window.clearInterval(pollClosed);
      try {
        if (popup && !popup.closed) popup.close();
      } catch {
        /* ignore */
      }
    }

    window.addEventListener("message", onMessage);
  });
}

/* ----------------------------------------------- Meta OAuth */

export interface MetaTokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
}

function buildMetaAuthUrl(scope: string, state: string): string {
  const u = new URL("https://www.facebook.com/v21.0/dialog/oauth");
  u.searchParams.set("client_id", config.metaAppId);
  u.searchParams.set("redirect_uri", config.metaOauthRedirectUri);
  u.searchParams.set("response_type", "code");
  u.searchParams.set("scope", scope);
  u.searchParams.set("state", state);
  return u.toString();
}

export function connectMeta(scope: string): Promise<MetaTokenResponse> {
  return new Promise((resolve, reject) => {
    if (!config.metaAppId) {
      reject(new Error("meta_app_id_not_configured"));
      return;
    }
    const state = randomState();
    const url = buildMetaAuthUrl(scope, state);
    const popup = window.open(
      url,
      "agencylens-oauth-meta",
      "width=520,height=640",
    );
    if (!popup) {
      reject(new Error("popup_blocked"));
      return;
    }
    const allowedOrigins = new Set<string>([
      new URL(config.metaOauthRedirectUri).origin,
    ]);
    let resolved = false;
    const onMessage = (event: MessageEvent) => {
      if (!allowedOrigins.has(event.origin)) return;
      const data = event.data as
        | { type?: string; state?: string; tokens?: MetaTokenResponse; error?: string; provider?: string }
        | null;
      if (!data || data.provider !== "meta") return;
      if (data.state !== state) return;
      if (data.type === "agencylens:oauth-success" && data.tokens) {
        cleanup();
        resolve(data.tokens);
      } else if (data.type === "agencylens:oauth-error") {
        cleanup();
        reject(new Error(data.error || "oauth_failed"));
      }
    };
    const pollClosed = window.setInterval(() => {
      if (popup && popup.closed && !resolved) {
        cleanup();
        reject(new Error("popup_closed"));
      }
    }, 500);
    function cleanup() {
      resolved = true;
      window.removeEventListener("message", onMessage);
      window.clearInterval(pollClosed);
      try {
        if (popup && !popup.closed) popup.close();
      } catch {
        /* ignore */
      }
    }
    window.addEventListener("message", onMessage);
  });
}

/* ------------------------------------------ Google token refresh */

/** Refresh a Google access token via the OAuth Worker. */
export async function refreshGoogleToken(
  refreshToken: string,
): Promise<GoogleTokenResponse> {
  const res = await fetch(`${config.oauthWorkerBase}/oauth/refresh/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`refresh failed: ${res.status} ${body.slice(0, 200)}`);
  }
  return (await res.json()) as GoogleTokenResponse;
}
